import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { POCKETSUITE_USER_ID } from '../utils/constants'
import { DATE_SHORT_ISO_FORMAT } from '../utils/dates'
import { Adjustment } from './Adjustment'
import { Alias } from './Alias'
import { BaseRecord } from './BaseRecord'
import { Bookable } from './Bookable'
import { Booking } from './Booking'
import { Client } from './Client'
import { Contact } from './Contact'
import { Contract } from './Contract'
import { Deposit } from './Deposit'
import { Edition } from './Edition'
import { Estimate } from './Estimate'
import { Feature } from './Feature'
import { Field } from './Field'
import { Gift } from './Gift'
import { Invoice } from './Invoice'
import { Item } from './Item'
import { Job } from './Job'
import { Lesson } from './Lesson'
import { Message } from './Message'
import { Note } from './Note'
import { Order } from './Order'
import { PSRecord } from './PSRecord'
import { Package } from './Package'
import { Payee } from './Payee'
import { Payment } from './Payment'
import { PERSON_TYPE } from './Person'
import { PhoneNumber } from './PhoneNumber'
import { Reader } from './Reader'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { Requirement } from './Requirement'
import { Resource } from './Resource'
import { Review } from './Review'
import { SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { Schedule } from './Schedule'
import { Subaccount } from './Subaccount'
import { Subscription } from './Subscription'
import { Task } from './Task'
import { Team } from './Team'
import { Transaction } from './Transaction'
import { Type } from './Type'
import { User } from './User'

export class ComputeFactory {
    private static instance: ComputeFactory | null = null
    public static getInstance() {
        if (ComputeFactory.instance == null) {
            ComputeFactory.instance = new ComputeFactory()
        }
        return ComputeFactory.instance
    }

    async fetchPremiumSubscription(): Promise<Subscription | undefined> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('subscriptions')
        sql.filterBy('owner=?', POCKETSUITE_USER_ID)
        sql.filterBy('client=?', PSApplication.me().id!)
        sql.filterBy('confirmedDate IS NOT NULL')
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('createdDate', true)
        sql.fk = Subscription.fk
        const resultData = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        if (resultData) return new Subscription(JSON.parse(resultData['jsonData']))
        return undefined
    }

    async allowFreeTrial(): Promise<boolean> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('subscriptions')
        sql.filterBy('owner=?', POCKETSUITE_USER_ID)
        sql.filterBy('client=?', PSApplication.me().id!)
        sql.filterBy('confirmedDate IS NOT NULL')
        sql.orderBy('createdDate', true)
        const resultSet = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of resultSet) {
            const subscription = new Subscription(JSON.parse(result['jsonData']))
            if (
                !subscription.cancelledDate ||
                moment().diff(subscription.cancelledDate, 'days') < 60
            )
                return false
        }
        return true
    }

    async fetchPremiumNumber(): Promise<PhoneNumber | undefined> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addTable('numbers')
        sql.filterBy('owner=?', PSApplication.owner().id!)
        sql.filterBy('person=?', PSApplication.me().id!)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('createdDate', true)
        sql.fk = PhoneNumber.fk
        const resultData = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        if (resultData) return new PhoneNumber(JSON.parse(resultData['jsonData']))
        return undefined
    }

    async fetchFeature(featureId: string): Promise<Feature | undefined> {
        if (!featureId) return undefined
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('features')
        sql.filterBy('id=?', featureId)
        sql.fk = Feature.fk
        const resultData = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        if (resultData) return new Feature(JSON.parse(resultData['jsonData']))
        return undefined
    }

    async fetchAlias(): Promise<Alias | undefined> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('aliases')
        sql.filterBy('owner=?', PSApplication.me().id!)
        sql.fk = Alias.fk
        const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return result ? new Alias(JSON.parse(result['jsonData'])) : undefined
    }

    async fetchReviewByTransaction(trandoc: Booking | Order): Promise<Review | undefined> {
        if (!(trandoc instanceof Booking) && !(trandoc instanceof Order)) return undefined

        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('reviews')
        if (trandoc instanceof Booking) {
            sql.filterBy('booking=?', trandoc.id!)
            sql.fk = Booking.fk
        }
        else if (trandoc instanceof Order) {
            sql.filterBy('"order"=?', trandoc.id!)
            sql.fk = Order.fk
        }
        const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return result ? new Review(JSON.parse(result['jsonData'])) : undefined
    }

    async fetchSubaccounts(client: User): Promise<Subaccount[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('subaccounts')
        sql.filterBy('owner = ?', PSApplication.owner().id!)
        sql.filterBy('client = ?', client.id!)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('name')
        sql.fk = Subaccount.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Subaccount(JSON.parse(result['jsonData'])))
    }

    async fetchSubaccountsForOwner(owner: User): Promise<Subaccount[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('subaccounts')
        sql.filterBy('client = ?', PSApplication.me().id!)
        sql.filterBy('owner = ?', owner.id!)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('name')
        sql.fk = Subaccount.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Subaccount(JSON.parse(result['jsonData'])))
    }

    async fetchNotes(person: User | string): Promise<Note[]> {
        const id = person instanceof User ? person.id! : person
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('notes')
        sql.filterBy('person=?', id)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('lastModifiedDate', true)
        sql.fk = Note.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Note(JSON.parse(result['jsonData'])))
    }

    async fetchBookingNotes(booking: Booking | string): Promise<Note[]> {
        const id = booking instanceof Booking ? booking.id! : booking
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('notes')
        sql.filterBy('booking=?', id)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('lastModifiedDate', true)
        sql.fk = Note.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Note(JSON.parse(result['jsonData'])))
    }

    async fetchLocationNotes(location: string): Promise<Note[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('notes')
        sql.filterBy('location=?', location)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('lastModifiedDate', true)
        sql.fk = Note.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Note(JSON.parse(result['jsonData'])))
    }

    async fetchEstimateNotes(estimate: Estimate | string): Promise<Note[]> {
        const id = estimate instanceof Estimate ? estimate.id! : estimate
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('notes')
        sql.filterBy('estimate=?', id)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('lastModifiedDate', true)
        sql.fk = Note.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Note(JSON.parse(result['jsonData'])))
    }

    async fetchOrderNotes(order: Order): Promise<Note[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('notes')
        sql.filterBy('notes."order"=?', order.id!)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('lastModifiedDate', true)
        sql.fk = Note.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Note(JSON.parse(result['jsonData'])))
    }

    async fetchSubaccountNotes(subaccount: Subaccount): Promise<Note[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('notes')
        sql.filterBy('notes.subaccount=?', subaccount.id!)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('lastModifiedDate', true)
        sql.fk = Note.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        return results.map((result) => new Note(JSON.parse(result['jsonData'])))
    }

    async fetchTeamByPeople(people: User[]): Promise<Team> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('teams')
        sql.filterBy(
            '(select count(*) from members where team=teams.id and cancelledDate IS NULL)=?',
            people.length
        )
        for (const member of people)
            sql.filterBy(
                'exists(select 1 from members where team=teams.id and person=?)',
                member.id!
            )
        sql.fk = Team.fk
        const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return result
            ? new Team(JSON.parse(result['jsonData']))
            : new Team(undefined, { members: people })
    }

    async fetchEdition(id: string, owner: User): Promise<Edition | undefined> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('editions')
        if (id) sql.filterBy('id=?', id)
        else if (owner) sql.filterBy('owner=?', owner.id!)
        sql.fk = Edition.fk
        const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return result ? new Edition(JSON.parse(result['jsonData'])) : undefined
    }

    async fetchPayableBookings(payee: User): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('date')
        sql.addField('jsonData')
        sql.addTable('bookings')
        sql.filterBy('bookings.confirmedDate IS NOT NULL')
        sql.filterBy('bookings.invoicedDate IS NULL')
        sql.filterBy('bookings.source IS NULL')
        sql.filterBy('bookings.paidDate IS NULL')
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.filterBy('bookings.vendor=?', payee.id!)
        sql.filterBy('bookings.client=?', PSApplication.owner().id!)
        sql.orderBy('date', true)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results) {
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        }
        return bookingsArray
    }

    async fetchScheduleBookings(schedule: Schedule): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('bookings')
        if (schedule) sql.filterBy('schedule=?', schedule.id!)
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.orderBy('date', true)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results) {
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        }
        return bookingsArray
    }

    async fetchJobs(person: User, booking: Booking, forPayable: boolean): Promise<Job[]> {
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField('jobs.date')
        sql.addField('jobs.confirmedDate')
        sql.addField('jobs.jsonData')
        sql.addTable('bookings')
        sql.addTable('jobs', 'jobs.booking=bookings.id')
        sql.filterBy('bookings.confirmedDate IS NOT NULL')
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.filterBy('jobs.confirmedDate IS NOT NULL')
        if (forPayable) {
            sql.filterBy('jobs.paidDate IS NULL')
            sql.filterBy("jobs.availableDate <= date('now')")
            sql.filterBy('jobs.date >= ?', moment().subtract(1, 'months').format('YYYY-MM-DD'))
            sql.filterBy('jobs.date <= ?', moment().add(1, 'months').format('YYYY-MM-DD'))
        }
        if (person) sql.filterBy('jobs.person=?', person.id!)
        if (booking) sql.filterBy('jobs.booking=?', booking.id!)
        sql.filterBy('jobs.owner=?', owner.id!)

        if (forPayable && !booking) {
            const sql2 = new SQLBuilder()
            sql2.addField('jobs.date')
            sql2.addField('jobs.confirmedDate')
            sql2.addField('jobs.jsonData')
            sql2.addTable('lessons')
            sql2.addTable('jobs', 'jobs.lesson=lessons.id')
            sql2.filterBy('lessons.cancelledDate IS NULL')
            sql2.filterBy('jobs.confirmedDate IS NOT NULL')
            sql2.filterBy('jobs.paidDate IS NULL')
            sql2.filterBy("jobs.availableDate <= date('now')")
            sql2.filterBy('jobs.date >= ?', moment().subtract(1, 'months').format('YYYY-MM-DD'))
            sql2.filterBy('jobs.date <= ?', moment().add(1, 'months').format('YYYY-MM-DD'))
            if (person) sql2.filterBy('jobs.person=?', person.id!)
            sql2.filterBy('jobs.owner=?', owner.id!)
            sql2.fk = Job.fk
            sql.addUnion(sql2)

            const sql3 = new SQLBuilder()
            sql3.addField('jobs.date')
            sql3.addField('jobs.confirmedDate')
            sql3.addField('jobs.jsonData')
            sql3.addTable('jobs')
            sql3.filterBy("jobs.type in ('package','invoice','subscription')")
            sql3.filterBy('jobs.confirmedDate IS NOT NULL')
            sql3.filterBy('jobs.paidDate IS NULL')
            sql3.filterBy("jobs.availableDate <= date('now')")
            sql3.filterBy('jobs.date >= ?', moment().subtract(1, 'months').format('YYYY-MM-DD'))
            sql3.filterBy('jobs.date <= ?', moment().add(1, 'months').format('YYYY-MM-DD'))
            if (person) sql3.filterBy('jobs.person=?', person.id!)
            sql3.filterBy('jobs.owner=?', owner.id!)
            sql3.fk = Job.fk
            sql.addUnion(sql3)
        }

        sql.orderBy('jobs.date')
        sql.orderBy('jobs.confirmedDate')
        sql.fk = Job.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const jobsArray: Job[] = []
        for (const jobData of results) {
            jobsArray.push(new Job(JSON.parse(jobData['jsonData'])))
        }
        return jobsArray
    }

    async fetchRequirements(resource: Resource): Promise<Requirement[]> {
        const sql = new SQLBuilder()
        sql.addField('requirements.jsonData')
        sql.addTable('allocations')
        sql.addTable('requirements', 'allocations.requirement=requirements.id')
        sql.filterBy('allocations.booking IS NULL')
        sql.filterBy('requirements.cancelledDate IS NULL')
        sql.filterBy('allocations.resource = ?', resource!.id!)
        sql.orderBy('CAST(requirements.seqnum as integer)')
        sql.fk = Requirement.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const requirementsArray: Requirement[] = []
        for (const requirementData of results) {
            requirementsArray.push(new Requirement(JSON.parse(requirementData['jsonData'])))
        }
        return requirementsArray
    }

    async fetchBills(person: User): Promise<Transaction[]> {
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField("'invoice'", 'type')
        sql.addField('invoices.date')
        sql.addField('invoices.jsonData')
        sql.addTable('invoices')
        sql.filterBy('invoices.total > 0')
        sql.filterBy("invoices.type = 'invoice'")
        sql.filterBy('invoices.paidDate IS NULL')
        sql.filterBy('invoices.cancelledDate IS NULL')
        sql.filterBy('invoices.vendor=?', person.id!)
        sql.filterBy('invoices.client=?', owner.id!)
        sql.fk = Invoice.fk

        const sql2 = new SQLBuilder()
        sql2.addField("'package'", 'type')
        sql2.addField('packages.createdDate', 'date')
        sql2.addField('packages.jsonData')
        sql2.addTable('packages')
        sql2.filterBy('packages.total > 0')
        sql2.filterBy('packages.paidDate IS NULL')
        sql2.filterBy('packages.cancelledDate IS NULL')
        sql2.filterBy('packages.owner=?', person.id!)
        sql2.filterBy('packages.client=?', owner.id!)
        sql2.fk = Package.fk
        sql.addUnion(sql2)

        const sql3 = new SQLBuilder()
        sql3.addField("'gift'", 'type')
        sql3.addField('gifts.createdDate', 'date')
        sql3.addField('gifts.jsonData')
        sql3.addTable('gifts')
        sql3.filterBy('gifts.total > 0')
        sql3.filterBy('gifts.paidDate IS NULL')
        sql3.filterBy('gifts.cancelledDate IS NULL')
        sql3.filterBy('gifts.owner=?', person.id!)
        sql3.filterBy('gifts.client=?', owner.id!)
        sql3.fk = Gift.fk
        sql.addUnion(sql3)

        const sql4 = new SQLBuilder()
        sql4.addField("'booking'", 'type')
        sql4.addField('bookings.date')
        sql4.addField('bookings.jsonData')
        sql4.addTable('bookings')
        sql4.filterBy('bookings.total > 0')
        sql4.filterBy('bookings.deposit > 0')
        sql4.filterBy("bookings.status='sentQuote'")
        sql4.filterBy('bookings.paidDate IS NULL')
        sql4.filterBy('bookings.cancelledDate IS NULL')
        sql4.filterBy('bookings.confirmedDate IS NULL')
        sql4.filterBy('bookings.paidDepositDate IS NULL')
        sql4.filterBy('bookings.vendor=?', person.id!)
        sql4.filterBy('bookings.client=?', owner.id!)
        sql4.fk = Booking.fk
        sql.addUnion(sql4)

        const sql5 = new SQLBuilder()
        sql5.addField("'estimate'", 'type')
        sql5.addField('estimates.date')
        sql5.addField('estimates.jsonData')
        sql5.addTable('estimates')
        sql5.filterBy('estimates.total > 0')
        sql5.filterBy('estimates.deposit > 0')
        sql5.filterBy("estimates.status='sentQuote'")
        sql5.filterBy('estimates.paidDate IS NULL')
        sql5.filterBy('estimates.cancelledDate IS NULL')
        sql5.filterBy('estimates.confirmedDate IS NULL')
        sql5.filterBy('estimates.paidDepositDate IS NULL')
        sql5.filterBy('estimates.vendor=?', person.id!)
        sql5.filterBy('estimates.client=?', owner.id!)
        sql5.fk = Estimate.fk
        sql.addUnion(sql5)

        const sql6 = new SQLBuilder()
        sql6.addField("'subscription'", 'type')
        sql6.addField('subscriptions.date')
        sql6.addField('subscriptions.jsonData')
        sql6.addTable('subscriptions')
        sql6.filterBy('subscriptions.total > 0')
        sql6.filterBy('subscriptions.cancelledDate IS NULL')
        sql6.filterBy('subscriptions.confirmedDate IS NULL')
        sql6.filterBy('subscriptions.vendor=?', person.id!)
        sql6.filterBy('subscriptions.client=?', owner.id!)
        sql6.fk = Subscription.fk
        sql.addUnion(sql6)

        sql.orderBy('date', true)
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const resultsArray: Transaction[] = []
        for (const billData of results) {
            if ('booking' === billData['type']) {
                const booking = new Booking(JSON.parse(billData['jsonData']))
                if (booking.amount ?? 0 > 0) resultsArray.push(booking)
            }
            else if ('invoice' === billData['type']) {
                const invoice = new Invoice(JSON.parse(billData['jsonData']))
                if (invoice.amount ?? 0 > 0) resultsArray.push(invoice)
            }
            else if ('package' === billData['type']) {
                const aPackage = new Package(JSON.parse(billData['jsonData']))
                if (aPackage.amount ?? 0 > 0) resultsArray.push(aPackage)
            }
            else if ('gift' === billData['type']) {
                const gift = new Gift(JSON.parse(billData['jsonData']))
                if (gift.amount ?? 0 > 0) resultsArray.push(gift)
            }
            else if ('estimate' === billData['type']) {
                const estimate = new Estimate(JSON.parse(billData['jsonData']))
                if (estimate.amount ?? 0 > 0) resultsArray.push(estimate)
            }
            else if ('subscription' === billData['type']) {
                const subscription = new Subscription(JSON.parse(billData['jsonData']))
                if (subscription.amount ?? 0 > 0) resultsArray.push(subscription)
            }
        }
        return resultsArray
    }

    async fetchClassBookingsByItem(item: Item, client: User): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('bookings.date')
        sql.addField('bookings.jsonData')
        sql.addTable('bookings')
        if (client) sql.filterBy('bookings.client=?', client.id!)
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.filterBy('bookings.lesson in (select id from lessons where item=?)', item.id!)
        sql.orderBy('bookings.date', false)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results)
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        return bookingsArray
    }

    async fetchClassBookings(lesson: Lesson, status?: string, client?: User): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('bookings.date')
        sql.addField('bookings.jsonData')
        sql.addTable('bookings')
        sql.addTable('users', 'users.id=bookings.client')
        if (client) sql.filterBy('bookings.client=?', client.id!)
        if ('confirmed' === status) sql.filterBy('bookings.confirmedDate IS NOT NULL')
        else if ('booked' === status) sql.filterBy("bookings.status != 'waitListed'")
        else if ('waitListed' === status) sql.filterBy("bookings.status = 'waitListed'")
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.filterBy('bookings.lesson=?', lesson.id!)
        sql.orderBy('waitListed' === status ? 'bookings.createdDate' : 'users.name', false)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results)
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        return bookingsArray
    }

    async fetchItemBookings(item: Item, confirmedOnly: boolean, client?: User): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('bookings.date')
        sql.addField('bookings.jsonData')
        sql.addTable('bookings')
        sql.addTable('users', 'users.id=bookings.client')
        if (client) sql.filterBy('bookings.client=?', client.id!)
        if (confirmedOnly) sql.filterBy('bookings.confirmedDate IS NOT NULL')
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.filterBy('bookings.item=?', item.id!)
        sql.orderBy('users.name', false)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results)
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        return bookingsArray
    }

    async fetchClasses(classString: string, date: moment.Moment | undefined): Promise<Lesson[]> {
        const results: Lesson[] = []
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('lessons')
        sql.filterBy('lessons.item = ?', classString)
        if (date) {
            sql.filterBy('lessons.date >= ?', date.format('YYYY-MM-DD'))
        }
        sql.filterBy('lessons.cancelledDate IS NULL')
        sql.orderBy('lessons.date', false)
        sql.fk = Lesson.fk
        const l = await SQLiteFactory.getInstance().executeSql(sql)
        for (const obj of l) results.push(new Lesson(JSON.parse(obj['jsonData'])))
        return results
    }

    async fetchPackageBookings(aPackage: Package): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('date')
        sql.addField('jsonData')
        sql.addTable('bookings')
        sql.filterBy('bookings.packageCancelledDate IS NULL')
        sql.filterBy("bookings.status != 'waitListed'")
        sql.filterBy('package=?', aPackage.id!)
        sql.orderBy('date', true)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results)
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        return bookingsArray
    }

    async fetchSubscriptionPayments(subscription: Subscription): Promise<Payment[]> {
        const paymentsArray: Payment[] = []
        if (!subscription.id) return paymentsArray
        const sql = new SQLBuilder()
        sql.addField('createdDate')
        sql.addField('jsonData')
        sql.addTable('payments')
        sql.filterBy('payments.cancelledDate IS NULL')
        sql.filterBy('payments.type=?', 'subscription')
        sql.filterBy('payments.subscription=?', subscription.id!)
        sql.orderBy('createdDate', true)
        sql.fk = Payment.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        for (const paymentData of results)
            paymentsArray.push(new Payment(JSON.parse(paymentData['jsonData'])))
        return paymentsArray
    }

    async fetchSubscriptionPackages(subscription: Subscription): Promise<Package[]> {
        const sql = new SQLBuilder()
        sql.addField('createdDate')
        sql.addField('jsonData')
        sql.addTable('packages')
        sql.filterBy('packages.cancelledDate IS NULL')
        sql.filterBy('subscription=?', subscription.id!)
        sql.orderBy('createdDate', true)
        sql.fk = Package.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const packagesArray: Package[] = []
        for (const packageData of results)
            packagesArray.push(new Package(JSON.parse(packageData['jsonData'])))
        return packagesArray
    }

    async fetchInvoicePayments(invoice: Invoice): Promise<Payment[]> {
        const sql = new SQLBuilder()
        sql.addField('createdDate')
        sql.addField('jsonData')
        sql.addTable('payments')
        sql.filterBy('payments.cancelledDate IS NULL')
        sql.filterBy('invoice=?', invoice.id!)
        sql.orderBy('createdDate', true)
        sql.fk = Payment.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const paymentsArray: Payment[] = []
        for (const paymentData of results)
            paymentsArray.push(new Payment(JSON.parse(paymentData['jsonData'])))
        return paymentsArray
    }

    async fetchPackagePayments(aPackage: Package): Promise<Payment[]> {
        const sql = new SQLBuilder()
        sql.addField('createdDate')
        sql.addField('jsonData')
        sql.addTable('payments')
        sql.filterBy('payments.cancelledDate IS NULL')
        sql.filterBy('package=?', aPackage.id!)
        sql.orderBy('createdDate', true)
        sql.fk = Payment.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const paymentsArray: Payment[] = []
        for (const paymentData of results)
            paymentsArray.push(new Payment(JSON.parse(paymentData['jsonData'])))
        return paymentsArray
    }

    async fetchDepositPayments(deposit: Deposit): Promise<Payment[]> {
        const sql = new SQLBuilder()
        sql.addField('createdDate')
        sql.addField('jsonData')
        sql.addTable('payments')
        sql.filterBy('deposit=?', deposit.id!)
        sql.orderBy('createdDate', true)
        sql.fk = Payment.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const paymentsArray: Payment[] = []
        for (const paymentData of results)
            paymentsArray.push(new Payment(JSON.parse(paymentData['jsonData'])))
        return paymentsArray
    }

    async fetchScheduledInvoices(invoice: Invoice): Promise<Invoice[]> {
        const sql = new SQLBuilder()
        sql.addField('createdDate')
        sql.addField('jsonData')
        sql.addTable('invoices')
        sql.filterBy('invoices.cancelledDate IS NULL')
        sql.filterBy('schedule=?', invoice.id!)
        sql.orderBy('createdDate', true)
        sql.fk = Invoice.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const invoicesArray: Invoice[] = []
        for (const invoiceData of results)
            invoicesArray.push(new Invoice(JSON.parse(invoiceData['jsonData'])))
        return invoicesArray
    }

    async fetchBillableBookings(client: User): Promise<Booking[]> {
        const sql = new SQLBuilder()
        sql.addField('date')
        sql.addField('jsonData')
        sql.addTable('bookings')
        sql.filterBy('bookings.confirmedDate IS NOT NULL')
        sql.filterBy('bookings.invoicedDate IS NULL')
        sql.filterBy('bookings.paidDate IS NULL')
        sql.filterBy("(bookings.package IS NULL OR bookings.package = '')")
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.filterBy('client=?', client.id!)
        sql.filterBy('vendor=?', PSApplication.owner().id!)
        sql.orderBy('date', true)
        sql.fk = Booking.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const bookingsArray: Booking[] = []
        for (const bookingData of results)
            bookingsArray.push(new Booking(JSON.parse(bookingData['jsonData'])))
        return bookingsArray
    }

    async fetchPaidPackages(
        clientString: string,
        ownerString: string,
        itemString: string
    ): Promise<Package[]> {
        if (!clientString) return []
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('jsonData')
        sql.addField(
            '(select coalesce(count(*),0) from bookings where packageCancelledDate IS NULL and package=packages.id)',
            'ct'
        )
        sql.addField(
            '(select coalesce(sum(duration),0) from bookings where packageCancelledDate IS NULL and package=packages.id)',
            'duration'
        )
        sql.addField('(select packageAll from items where id=packages.item)', 'package_all')
        if (itemString) {
            sql.addField(
                'coalesce((select duration from items where id=?),0)',
                'item_duration',
                itemString
            )
            sql.addField(
                'coalesce((select capacity from item_components where packages.item=item_components.item and item_components.component=?),0)',
                'item_capacity',
                itemString
            )
            sql.addField(
                '(select component from item_components where packages.item=item_components.item and item_components.component=?)',
                'component',
                itemString
            )
            sql.addField(
                'coalesce((select count(*) from lines join bookings on lines.trandoc=bookings.id where bookings.packageCancelledDate IS NULL and bookings.package=packages.id and lines.item=?),0)',
                'ct_item',
                itemString
            )
        }
        sql.addTable('packages')
        sql.filterBy('packages.owner = ?', ownerString)
        sql.filterBy('packages.customer = ?', clientString)
        sql.filterBy('packages.paidDate IS NOT NULL')
        sql.filterBy('packages.cancelledDate IS NULL')
        sql.filterBy('packages.completedDate IS NULL')
        sql.orderBy('expiredDate')
        sql.fk = Package.fk
        const resultSet = await SQLiteFactory.getInstance().executeSql(sql)
        const l: Package[] = []
        for (const packageData of resultSet) {
            const packageDataMap = new RecordDataMap(packageData)
            const aPackage = new Package(JSON.parse(packageDataMap.get('jsonData')))
            const expired = aPackage.expiredDate!.isBefore(moment())
            if ('session' === aPackage.capacityType) {
                const ct = packageDataMap.valueForIntKey('ct')!
                const exhausted = aPackage.capacity! <= ct
                if (
                    !exhausted &&
                    !expired &&
                    (!itemString ||
                        packageDataMap.valueForBoolKey('package_all') ||
                        (packageDataMap.hasValueForKey('component') &&
                            (packageDataMap.valueForIntKey('item_capacity') == 0 ||
                                (packageDataMap.valueForIntKey('ct_item') ?? 0) <
                                    (packageDataMap.valueForIntKey('item_capacity') ?? 0))))
                )
                    l.push(aPackage)
            }
            else {
                const duration = packageDataMap.valueForIntKey('duration')!
                const exhausted = aPackage.capacity! <= duration
                if (
                    !exhausted &&
                    !expired &&
                    (!itemString ||
                        packageDataMap.valueForBoolKey('package_all') ||
                        (packageDataMap.hasValueForKey('component') &&
                            (packageDataMap.valueForIntKey('item_capacity') == 0 ||
                                (packageDataMap.valueForIntKey('ct_item') ?? 0) +
                                    1 * (packageDataMap.valueForIntKey('item_duration') ?? 0) <=
                                    (packageDataMap.valueForIntKey('item_capacity') ?? 0))))
                )
                    l.push(aPackage)
            }
        }
        return l
    }

    async fetchPurchasesWithGift(giftString: string): Promise<Transaction[]> {
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addField("'booking'", 'type')
        sql.addField('bookings.date', 'date')
        sql.addTable('bookings')
        sql.filterBy('bookings.vendor = ?', owner.id!)
        sql.filterBy('bookings.gift = ?', giftString)
        sql.filterBy('bookings.cancelledDate IS NULL')
        sql.orderBy('date')
        sql.fk = Booking.fk

        const sql2 = new SQLBuilder()
        sql2.addField('jsonData')
        sql2.addField("'package'", 'type')
        sql2.addField('packages.paidDate', 'date')
        sql2.addTable('packages')
        sql2.filterBy('packages.vendor = ?', owner.id!)
        sql2.filterBy('packages.gift = ?', giftString)
        sql2.filterBy('packages.cancelledDate IS NULL')
        sql.addUnion(sql2)
        sql2.fk = Package.fk

        const sql3 = new SQLBuilder()
        sql3.addField('jsonData')
        sql3.addField("'invoice'", 'type')
        sql3.addField('invoices.paidDate', 'date')
        sql3.addTable('invoices')
        sql3.filterBy("invoices.type = 'invoice'")
        sql3.filterBy('invoices.vendor = ?', owner.id!)
        sql3.filterBy('invoices.gift = ?', giftString)
        sql3.filterBy('invoices.cancelledDate IS NULL')
        sql.addUnion(sql3)
        sql3.fk = Invoice.fk

        const sql4 = new SQLBuilder()
        sql4.addField('jsonData')
        sql4.addField("'subscription'", 'type')
        sql4.addField('subscriptions.confirmedDate', 'date')
        sql4.addTable('subscriptions')
        sql4.filterBy('subscriptions.vendor = ?', owner.id!)
        sql4.filterBy('subscriptions.gift = ?', giftString)
        sql4.filterBy('subscriptions.cancelledDate IS NULL')
        sql.addUnion(sql4)
        sql4.fk = Subscription.fk

        const sql5 = new SQLBuilder()
        sql5.addField('jsonData')
        sql5.addField("'order'", 'type')
        sql5.addField('orders.paidDate', 'date')
        sql5.addTable('orders')
        sql5.filterBy('orders.vendor = ?', owner.id!)
        sql5.filterBy('orders.gift = ?', giftString)
        sql5.filterBy('orders.cancelledDate IS NULL')
        sql.addUnion(sql5)
        sql5.fk = Order.fk

        const l: Transaction[] = []
        const resultSet = await SQLiteFactory.getInstance().executeSql(sql)
        for (const resultData of resultSet) {
            if (resultData['type'] === 'booking')
                l.push(new Booking(JSON.parse(resultData['jsonData'])))
            else if (resultData['type'] === 'package')
                l.push(new Package(JSON.parse(resultData['jsonData'])))
            else if (resultData['type'] === 'invoice')
                l.push(new Invoice(JSON.parse(resultData['jsonData'])))
            else if (resultData['type'] === 'order')
                l.push(new Order(JSON.parse(resultData['jsonData'])))
            else if (resultData['type'] === 'subscription')
                l.push(new Subscription(JSON.parse(resultData['jsonData'])))
        }
        return l
    }

    async fetchPaidGifts(client: User | undefined, owner: User | undefined): Promise<Gift[]> {
        if (!client || !owner) return []
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('gifts')
        sql.filterBy('gifts.owner = ?', owner.id!)
        sql.filterBy('gifts.customer = ?', client.id!)
        sql.filterBy('gifts.paidDate IS NOT NULL')
        sql.filterBy('gifts.cancelledDate IS NULL')
        sql.orderBy('expiredDate')
        sql.fk = Gift.fk

        const l: Gift[] = []
        const now = moment()
        const resultSet = await SQLiteFactory.getInstance().executeSql(sql)
        for (const resultData of resultSet) {
            const gift = new Gift(JSON.parse(resultData['jsonData']))
            if (
                (gift.balance ?? 0) > 0 &&
                (gift.expiredDate == undefined || gift.expiredDate?.isAfter(now))
            )
                l.push(gift)
        }
        return l
    }

    async fetchClient(
        owner: User | undefined,
        client: User | undefined
    ): Promise<Client | undefined> {
        if (!client || !owner) return undefined
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('clients')
        sql.filterBy('clients.cancelledDate IS NULL')
        sql.filterBy('clients.owner = ?', owner.id!)
        sql.filterBy('clients.person = ?', client.id!)
        sql.fk = Client.fk
        const resultSet = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return resultSet ? new Client(JSON.parse(resultSet['jsonData'])) : undefined
    }

    // The methods below are commented out because they are currently unused and take advantage
    // of a method on BaseRecord called `hasValueForKey`. The implementation of hasValueForKey
    // no longer works after the big model layer update of December 2024. In the interests of
    // time, we're just commenting out uses rather than reimplementing the method, but can
    // reimplement some new-model-layer-compatible version when needed.
    // async requireField(transaction: Booking | Order): Promise<boolean> {
    //     const owner = transaction.owner!
    //     const client = (await this.fetchClient(owner, transaction.client!)) as Client
    //     if (transaction instanceof Booking) {
    //         const fields = await RecordFactory.getInstance().loadFields(
    //             owner.id!,
    //             undefined,
    //             false,
    //             false,
    //             false,
    //             false,
    //             false,
    //             false,
    //             true /*publicOnly*/,
    //             false /*customOnly*/
    //         )
    //         return fields.some(
    //             (f) =>
    //                 f.required &&
    //                 f.checkoutBooking &&
    //                 f.allowEdit &&
    //                 ((f.booking &&
    //                     (f.client || !f.subaccount) &&
    //                     !Field.hasActiveValueForField(f, transaction)) ||
    //                     (f.client && !f.booking && !Field.hasActiveValueForField(f, client)))
    //         )
    //     }
    //     else if (transaction instanceof Order) {
    //         const fields = await RecordFactory.getInstance().loadFields(
    //             owner.id!,
    //             undefined,
    //             false,
    //             false,
    //             false,
    //             false,
    //             false,
    //             false,
    //             true /*publicOnly*/,
    //             false /*customOnly*/
    //         )
    //         return fields.some(
    //             (f) =>
    //                 f.required &&
    //                 f.checkoutOrder &&
    //                 f.allowEdit &&
    //                 ((f.order &&
    //                     (f.client || !f.subaccount) &&
    //                     !Field.hasActiveValueForField(f, transaction)) ||
    //                     (f.client && !f.order && !Field.hasActiveValueForField(f, client)))
    //         )
    //     }
    //     else {
    //         const fields = await RecordFactory.getInstance().loadFields(
    //             owner.id!,
    //             undefined,
    //             false,
    //             false,
    //             false,
    //             false,
    //             false,
    //             true /*other*/,
    //             true /*publicOnly*/,
    //             false /*customOnly*/
    //         )
    //         return fields.some(
    //             (f) =>
    //                 f.required &&
    //                 f.client &&
    //                 f.allowEdit &&
    //                 !Field.hasActiveValueForField(f, client)
    //         )
    //     }
    // }
    //
    // async requireRecordField(record: PSRecord): Promise<boolean> {
    //     const owner = record.owner!
    //     const client = (await this.fetchClient(owner, record.person!)) as Client
    //     const fields = await RecordFactory.getInstance().loadFields(
    //         owner.id!,
    //         undefined,
    //         false,
    //         false,
    //         false,
    //         false,
    //         false,
    //         true /*other*/,
    //         true /*publicOnly*/,
    //         false /*customOnly*/
    //     )
    //     return fields.some(
    //         (f) => f.required && f.client && f.allowEdit && !Field.hasActiveValueForField(f, client)
    //     )
    // }
    //
    // async requireSubaccount(transaction: Booking | Order): Promise<boolean> {
    //     const owner = transaction.owner!
    //     if (owner.allowSubaccount) {
    //         const subaccount = transaction.subaccount
    //         if (subaccount) {
    //             if (transaction instanceof Booking) {
    //                 const fields = await RecordFactory.getInstance().loadFields(
    //                     owner.id!,
    //                     undefined,
    //                     false,
    //                     false,
    //                     false,
    //                     true /*subaccount*/,
    //                     false,
    //                     false,
    //                     true /*publicOnly*/,
    //                     false /*customOnly*/
    //                 )
    //                 return fields.some(
    //                     (f) =>
    //                         f.required &&
    //                         f.checkoutBooking &&
    //                         f.allowEdit &&
    //                         !f.client &&
    //                         ((f.booking && !Field.hasActiveValueForField(f, transaction)) ||
    //                             (!f.booking && !Field.hasActiveValueForField(f, subaccount)))
    //                 )
    //             }
    //             else if (transaction instanceof Order) {
    //                 const fields = await RecordFactory.getInstance().loadFields(
    //                     owner.id!,
    //                     undefined,
    //                     false,
    //                     false,
    //                     false,
    //                     true /*subaccount*/,
    //                     false,
    //                     false,
    //                     true /*publicOnly*/,
    //                     false /*customOnly*/
    //                 )
    //                 return fields.some(
    //                     (f) =>
    //                         f.required &&
    //                         f.checkoutOrder &&
    //                         f.allowEdit &&
    //                         !f.client &&
    //                         ((f.order && !Field.hasActiveValueForField(f, transaction)) ||
    //                             (!f.order && !Field.hasActiveValueForField(f, subaccount)))
    //                 )
    //             }
    //             else {
    //                 const fields = await RecordFactory.getInstance().loadFields(
    //                     owner.id!,
    //                     undefined,
    //                     false,
    //                     false,
    //                     false,
    //                     true /*subaccount*/,
    //                     false,
    //                     true /*other*/,
    //                     true /*publicOnly*/,
    //                     false /*customOnly*/
    //                 )
    //                 return fields.some(
    //                     (f) =>
    //                         f.required &&
    //                         f.allowEdit &&
    //                         !Field.hasActiveValueForField(f, subaccount)
    //                 )
    //             }
    //         }
    //         return transaction instanceof Booking
    //     }
    //     return false
    // }
    //
    // async requireRecordSubaccount(record: PSRecord): Promise<boolean> {
    //     const owner = record.owner!
    //     if (owner.allowSubaccount) {
    //         const subaccount = record.subaccount
    //         if (subaccount) {
    //             const fields = await RecordFactory.getInstance().loadFields(
    //                 owner.id!,
    //                 undefined,
    //                 false,
    //                 false,
    //                 false,
    //                 true /*subaccount*/,
    //                 false,
    //                 true /*other*/,
    //                 true /*publicOnly*/,
    //                 false /*customOnly*/
    //             )
    //             return fields.some(
    //                 (f) =>
    //                     f.required &&
    //                     !f.client &&
    //                     f.allowEdit &&
    //                     !Field.hasActiveValueForField(f, subaccount)
    //             )
    //         }
    //     }
    //     return false
    // }

    async requireSignatureByTransaction(transaction: Booking | Order): Promise<boolean> {
        if (!transaction || transaction.signedDate) return false
        return await this.requireSignature(transaction.client!, transaction.contract!)
    }

    async requireSignature(client: User, contract: Contract): Promise<boolean> {
        if (!contract) return false
        if (contract.alwaysRequire) return true
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('signedDate')
        sql.addTable('signatures')
        sql.filterBy('signatures.person = ?', client.id!)
        sql.filterBy('signatures.contract = ?', contract.id!)
        sql.filterBy('signatures.cancelledDate IS NULL')
        sql.filterBy('signatures.signedDate IS NOT NULL')
        sql.orderBy('signatures.signedDate', true)
        const resultSet = new RecordDataMap(
            await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        )
        const expires = contract.expires
        if (!resultSet || !resultSet.hasValueForKey('id')) return true
        if (!expires) return false
        const d = moment(resultSet.valueForTimeStampKey('signedDate'))
        const field = expires.endsWith('m') ? 'months' : 'years'
        const expiredDate = d.add(parseInt(expires.replace('m', '').replace('y', '')), field)
        return expiredDate.isBefore(moment())
    }

    async requireRecordSignature(record: PSRecord): Promise<boolean> {
        if (!record || record.signedDate) return false
        const contract = record.contract
        if (!contract) return false
        if (contract.alwaysRequire) return true
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('signedDate')
        sql.addTable('signatures')
        sql.filterBy('signatures.person = ?', record.person!.id!)
        sql.filterBy('signatures.contract = ?', contract.id!)
        sql.filterBy('signatures.cancelledDate IS NULL')
        sql.filterBy('signatures.signedDate IS NOT NULL')
        sql.orderBy('signatures.signedDate', true)
        const resultSet = new RecordDataMap(
            await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        )
        const expires = contract.expires
        if (!resultSet || !resultSet.hasValueForKey('id')) return true
        if (!expires) return false
        const d = moment(resultSet.valueForTimeStampKey('signedDate'))
        const field = expires.endsWith('m') ? 'months' : 'years'
        const expiredDate = d.add(parseInt(expires.replace('m', '').replace('y', '')), field)
        return expiredDate.isBefore(moment())
    }

    async requireCaptureByTransaction(transaction: Booking | Order): Promise<boolean> {
        if (!transaction || transaction.capturedDate) return false
        return await this.requireCapture(transaction.client!, transaction.form!)
    }

    async requireCapture(client: User, form: Type): Promise<boolean> {
        if (!form) return false
        if (form.alwaysRequire) return true
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addField('completedDate')
        sql.addTable('records')
        sql.filterBy('records.person = ?', client.id!)
        sql.filterBy('records.type = ?', form.id!)
        sql.filterBy('records.cancelledDate IS NULL')
        sql.filterBy('records.completedDate IS NOT NULL')
        sql.orderBy('records.completedDate', true)
        const resultSet = new RecordDataMap(
            await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        )
        const expires = form.expires
        if (!resultSet || !resultSet.hasValueForKey('id')) return true
        if (!expires) return false
        const d = moment(resultSet.valueForTimeStampKey('completedDate'))
        const field = expires.endsWith('m') ? 'months' : 'years'
        const expiredDate = d.add(parseInt(expires.replace('m', '').replace('y', '')), field)
        return expiredDate.isBefore(moment())
    }

    async fetchFrom(
        fromDate: moment.Moment,
        toDate: moment.Moment,
        person?: User,
        item?: Item,
        matching?: string[]
    ): Promise<Map<moment.Moment, string[]>> {
        const from = fromDate.format(DATE_SHORT_ISO_FORMAT)
        const to = toDate.format(DATE_SHORT_ISO_FORMAT)
        const me = PSApplication.me()
        const owner = PSApplication.owner()
        const sql = new SQLBuilder()
        sql.addField('bookings.id', 'id')
        sql.addField('bookings.date', 'date')
        sql.addField('bookings.endDate', 'endDate')
        sql.addField("'booking'", 'type')
        sql.addTable('bookings')
        sql.filterBy('bookings.cancelledDate IS NULL')
        if (item)
            sql.filterBy('bookings.vendor IS NULL') // hack to ensure no booking rows are returned (for classes)
        else if (person) {
            sql.addTable('jobs', 'jobs.booking=bookings.id')
            sql.filterBy('jobs.person = ?', person.id!)
            sql.filterBy('bookings.vendor = ?', owner.id!)
        }
        else if (me.client) sql.filterBy('bookings.client = ?', me.id!)
        else if (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE) {
            sql.addTable('jobs', 'jobs.booking=bookings.id')
            sql.filterBy('jobs.person = ?', me.id!)
            sql.filterBy('bookings.vendor = ?', owner.id!)
        }
        else {
            sql.filterBy(
                '((bookings.lesson IS NULL AND bookings.vendor = ?) OR (bookings.lesson IS NOT NULL AND bookings.vendor != ?))',
                owner.id!,
                owner.id!
            )
            sql.filterBy(
                "((bookings.item IS NULL AND bookings.vendor = ?) OR EXISTS(SELECT 1 FROM items WHERE items.id=bookings.item and owner = ? and (type !='reservation' or startDate is null)))",
                owner.id!,
                owner.id!
            )
        }
        if (matching?.length) {
            sql.filterBy('bookings.id in (' + matching.map(() => '?').join(',') + ')', ...matching)
        }

        const newSql = sql.clone()
        const newSql2 = sql.clone()
        newSql.filterBy('(bookings.date <= ? and bookings.endDate >= ?)', from, to)
        sql.addUnion(newSql)
        newSql2.filterBy(
            '(bookings.date < ? and bookings.endDate >= ? and bookings.endDate <= ?)',
            from,
            from,
            to
        )
        sql.addUnion(newSql2)
        sql.filterBy('(bookings.date >= ? and bookings.date < ?)', from, to)

        if (!item) {
            const sqlEvent = new SQLBuilder()
            sqlEvent.addField('id')
            sqlEvent.addField('date')
            sqlEvent.addField('endDate')
            sqlEvent.addField("'event'", 'type')
            sqlEvent.addTable('events')
            sqlEvent.filterBy('events.cancelledDate IS NULL')
            if (!matching || matching.length === 0 || person) {
                sqlEvent.filterBy('events.owner = ?', person ? person.id! : me.id!)
            }
            if (matching?.length) {
                sqlEvent.filterBy(
                    'events.id in (' + matching.map(() => '?').join(',') + ')',
                    ...matching
                )
            }
            const evtSql = sqlEvent.clone()
            const evtSql2 = sqlEvent.clone()

            evtSql.filterBy('(events.endDate >= ? and events.endDate <= ?)', from, to)
            sql.addUnion(evtSql)
            evtSql2.filterBy('(events.date <= ? and events.endDate >= ?)', from, to)
            sql.addUnion(evtSql2)
            sqlEvent.filterBy('(events.date >= ? and events.date <= ?)', from, to)
            sql.addUnion(sqlEvent)
        }

        if (item || me.sp || (person && person.id !== me.id) || me.client) {
            if (me.client || owner.allowClass) {
                const sqlClass = new SQLBuilder()
                sqlClass.addField('id')
                sqlClass.addField('date')
                sqlClass.addField('date', 'endDate')
                sqlClass.addField("'lesson'", 'type')
                sqlClass.addTable('lessons')
                sqlClass.filterBy('lessons.cancelledDate IS NULL')
                sqlClass.filterBy('lessons.date >= ?', from)
                sqlClass.filterBy('lessons.date < ?', to)
                if (item) sqlClass.filterBy('lessons.item = ?', item.id!)
                else if (me.client) {
                    sqlClass.filterBy(
                        'EXISTS(select 1 from payees where lessons.owner=payees.person and payees.owner = ? and payees.cancelledDate is null)',
                        me.id!
                    )
                    sqlClass.filterBy(
                        'NOT EXISTS(select 1 from bookings where bookings.cancelledDate is null and bookings.lesson=lessons.id and bookings.client=?)',
                        me.id!
                    )
                    sqlClass.filterBy(
                        "EXISTS(select 1 from items where items.id=lessons.item and items.type='class' and items.isPublic = ? and items.cancelledDate is null)",
                        true
                    )
                }
                else if (person && person.id !== me.id)
                    sqlClass.filterBy('lessons.employee = ?', person.id!)
                else if (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE)
                    sqlClass.filterBy('lessons.employee = ?', me.id!)
                else sqlClass.filterBy('lessons.owner = ?', owner.id!)
                if (matching?.length) {
                    sqlClass.filterBy(
                        'lessons.id in (' + matching.map(() => '?').join(',') + ')',
                        ...matching
                    )
                }
                sql.addUnion(sqlClass)
            }

            if (me.client || (owner.allowReservation && me.perm > User.PERM_ROLE_EMPLOYEE)) {
                const sqlItem = new SQLBuilder()
                sqlItem.addField('items.id')
                sqlItem.addField('items.startDate', 'date')
                sqlItem.addField('items.endDate', 'endDate')
                sqlItem.addField("'reservation'", 'type')
                sqlItem.addTable('items')
                sqlItem.filterBy('items.cancelledDate IS NULL')
                sqlItem.filterBy("items.type='reservation'")
                if (item) sqlItem.filterBy('items.id = ?', item.id!)
                else if (me.client) {
                    sqlItem.filterBy(
                        'NOT EXISTS(select 1 from bookings where bookings.cancelledDate is null and bookings.item=items.id and bookings.client=?)',
                        me.id!
                    )
                    sqlItem.filterBy(
                        'EXISTS(select 1 from payees where items.owner=payees.person and payees.owner = ? and payees.cancelledDate is null)',
                        me.id!
                    )
                    sqlItem.filterBy('items.isPublic = ?', true)
                }
                else sqlItem.filterBy('items.owner = ?', owner.id!)
                if (matching?.length) {
                    sqlItem.filterBy(
                        'items.id in (' + matching.map(() => '?').join(',') + ')',
                        ...matching
                    )
                }
                const newItemSql = sqlItem.clone()
                const newItemSql2 = sqlItem.clone()
                sqlItem.filterBy('(items.endDate >= ? and items.endDate <= ?)', from, to)
                sql.addUnion(sqlItem)
                newItemSql.filterBy('(items.startDate <= ? and items.endDate >= ?)', from, to)
                sql.addUnion(newItemSql)
                newItemSql2.filterBy('(items.startDate >= ? and items.startDate <= ?)', from, to)
                sql.addUnion(newItemSql2)
            }
        }
        if (!item && (me.client || owner.allowProduct)) {
            const sqlOrder = new SQLBuilder()
            sqlOrder.addField('orders.id')
            sqlOrder.addField('orders.date', 'date')
            sqlOrder.addField('orders.endDate', 'endDate')
            sqlOrder.addField("'order'", 'type')
            sqlOrder.addTable('orders')
            sqlOrder.filterBy('orders.type = ?', 'pickup')
            sqlOrder.filterBy('orders.cancelledDate IS NULL')
            if (person || (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE)) {
                sqlOrder.filterBy('orders.employee = ?', person ? person.id! : me.id!)
                sqlOrder.filterBy('orders.vendor = ?', owner.id!)
            }
            else if (me.client) sqlOrder.filterBy('orders.client = ?', me.id!)
            else sqlOrder.filterBy('orders.vendor = ?', owner.id!)
            sqlOrder.filterBy('(orders.date >= ? and orders.date < ?)', from, to)
            if (matching?.length) {
                sqlOrder.filterBy(
                    'orders.id in (' + matching.map(() => '?').join(',') + ')',
                    ...matching
                )
            }
            sql.addUnion(sqlOrder)
        }
        if (me.sp && !item) {
            const sqlTask = new SQLBuilder()
            sqlTask.addField('tasks.id')
            sqlTask.addField('tasks.date', 'date')
            sqlTask.addField('tasks.date', 'endDate')
            sqlTask.addField("'task'", 'type')
            sqlTask.addTable('tasks')
            sqlTask.filterBy('tasks.cancelledDate IS NULL')
            sqlTask.filterBy('tasks.completedDate IS NULL')
            sqlTask.filterBy('tasks.owner = ?', owner.id!)
            if (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE)
                sqlTask.filterBy('tasks.employee = ?', me.id!)
            else if (person) sqlTask.filterBy('tasks.employee = ?', person.id!)
            sqlTask.filterBy('(tasks.date >= ? and tasks.date < ?)', from, to)
            if (matching?.length) {
                sqlTask.filterBy(
                    'tasks.id in (' + matching.map(() => '?').join(',') + ')',
                    ...matching
                )
            }
            sql.addUnion(sqlTask)
        }
        if (me.sp && !item) {
            const sqlBlock = new SQLBuilder()
            sqlBlock.addField('blocks.id')
            sqlBlock.addField('blocks.date', 'date')
            sqlBlock.addField('blocks.endDate', 'endDate')
            sqlBlock.addField("'block'", 'type')
            sqlBlock.addTable('blocks')
            sqlBlock.filterBy('blocks.cancelledDate IS NULL')
            sqlBlock.filterBy('blocks.owner = ?', owner.id!)
            if (!matching || matching.length === 0 || person) {
                sqlBlock.filterBy('blocks.person = ?', person ? person.id! : me.id!)
            }
            if (matching?.length) {
                sqlBlock.filterBy(
                    'blocks.id in (' + matching.map(() => '?').join(',') + ')',
                    ...matching
                )
            }
            const sqlBlock1 = sqlBlock.clone()
            const sqlBlock2 = sqlBlock.clone()

            sqlBlock1.filterBy('(blocks.endDate >= ? and blocks.endDate <= ?)', from, to)
            sql.addUnion(sqlBlock1)
            sqlBlock2.filterBy('(blocks.date <= ? and blocks.endDate >= ?)', from, to)
            sql.addUnion(sqlBlock2)
            sqlBlock.filterBy('(blocks.date >= ? and blocks.date <= ?)', from, to)
            sql.addUnion(sqlBlock)
        }
        sql.orderBy('date')

        const bookings = new Map<moment.Moment, string[]>() // mapping the date to the bookable.id
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of results) {
            const bookableId = result['id']

            let start = moment(result['date']).startOf('day')
            if (!start.isValid()) continue

            const type = result['type']
            // endDate is bogus for lessons and tasks (since we don't store the field) so we shouldn't consider here.
            if (type === 'lesson' || type === 'task') {
                if (!start.isBefore(fromDate) && !start.isAfter(toDate)) {
                    if (!bookings.has(start)) bookings.set(start, [])
                    bookings.get(start)!.push(bookableId)
                }
            }
            else {
                const end = moment(result['endDate']).startOf('day')
                if (!end.isValid()) continue

                do {
                    if (!start.isBefore(fromDate) && !start.isAfter(toDate)) {
                        if (!bookings.has(start)) bookings.set(start, [])
                        bookings.get(start)!.push(bookableId)
                    }
                    start = start.add(1, 'day')
                } while (!start.isAfter(end))
            }
        }
        return bookings
    }

    async fetchUsers(
        type: PERSON_TYPE,
        keywords: string | undefined,
        keys: string[]
    ): Promise<User[]> {
        const me = PSApplication.me()
        let cls: any = null
        const sql = new SQLBuilder()
        sql.addField('users.name')
        sql.addField('users.jsonData', 'jsonData')
        sql.addTable('users')
        if (type === PERSON_TYPE.PERSON_TYPE_CLIENT) {
            cls = Client
            sql.addField('clients.jsonData', 'typeData')
            sql.addTable('clients', 'clients.person=users.id')
            sql.filterBy('clients.cancelledDate IS NULL')
            sql.filterBy('clients.owner = ?', PSApplication.owner().id!)
            if (
                me.payee &&
                (me.perm === User.PERM_ROLE_CONTRACTOR ||
                    (me.perm === User.PERM_ROLE_EMPLOYEE &&
                        !(await RecordFactory.getInstance().fetchPayee(me.id!, false))
                            ?.showClients))
            )
                sql.filterBy('clients.employee = ?', me.id!)
            else if (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE)
                sql.filterBy('(clients.employee = ? OR clients.employee IS NULL)', me.id!)
        }
        else if (type === PERSON_TYPE.PERSON_TYPE_PAYEE) {
            cls = Payee
            sql.addField('payees.jsonData', 'typeData')
            sql.addTable('payees', 'payees.person=users.id')
            sql.filterBy('payees.cancelledDate IS NULL')
            sql.filterBy('payees.owner = ?', PSApplication.owner().id!)
        }
        else if (type === PERSON_TYPE.PERSON_TYPE_SUBSCRIBER) {
            cls = Payee
            sql.addField('subscribers.jsonData', 'typeData')
            sql.addTable('subscribers', 'subscribers.person=users.id')
            sql.filterBy('subscribers.cancelledDate IS NULL')
            sql.filterBy('subscribers.owner = ?', PSApplication.owner().id!)
        }
        else if (type === PERSON_TYPE.PERSON_TYPE_USER) {
            if (
                me.payee &&
                (me.perm === User.PERM_ROLE_CONTRACTOR ||
                    (me.perm === User.PERM_ROLE_EMPLOYEE &&
                        !(await RecordFactory.getInstance().fetchPayee(me.id!, false))
                            ?.showClients))
            )
                sql.filterBy(
                    'users.id in (select person from clients where cancelledDate IS NULL and employee = ? UNION select person from payees where cancelledDate IS NULL UNION select lastUser from status)',
                    me.id!
                )
            else if (me.payee && me.perm === User.PERM_ROLE_EMPLOYEE)
                sql.filterBy(
                    'users.id in (select person from clients where cancelledDate IS NULL and (employee = ? OR employee IS NULL) UNION select person from payees where cancelledDate IS NULL UNION select lastUser from status)',
                    me.id!
                )
            else
                sql.filterBy(
                    'users.id in (select person from clients where cancelledDate IS NULL UNION select person from payees where cancelledDate IS NULL UNION select person from subscribers where cancelledDate IS NULL UNION select lastUser from status)'
                )
        }

        if (keys.length > 0) {
            const args = keys.map(() => '?').join(',')
            sql.filterByWithArgs(`users.id in (${args})`, keys)
        }
        if (keywords) {
            const lastNameSql = sql.clone()
            const phoneSql = sql.clone()
            const emailSql = sql.clone()
            if (type === PERSON_TYPE.PERSON_TYPE_CLIENT) {
                const leadSourceSql = sql.clone()
                leadSourceSql.addTable('channels', 'clients.channel=channels.id')
                leadSourceSql.filterBy('channels.name LIKE ?', `${keywords}%`)
                sql.addUnion(leadSourceSql)
            }
            else if (type === PERSON_TYPE.PERSON_TYPE_PAYEE) {
                const industrySql = sql.clone()
                industrySql.addTable('industries', 'users.industry=industries.id')
                industrySql.filterBy('industries.name LIKE ?', `${keywords}%`)
                sql.addUnion(industrySql)
            }
            sql.filterBy('users.name LIKE ?', `${keywords}%`)
            lastNameSql.filterBy('users.lastName LIKE ?', `${keywords}%`)
            sql.addUnion(lastNameSql)
            phoneSql.filterBy('users.e164 LIKE ?', `+1${keywords}%`)
            sql.addUnion(phoneSql)
            emailSql.filterBy('users.email LIKE ?', `${keywords.toLowerCase()}%`)
            sql.addUnion(emailSql)
        }
        sql.orderBy('users.name')
        sql.fk = User.fk
        const results: User[] = []
        const l = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of l) {
            const user = new User(JSON.parse(result['jsonData']))
            if (cls) {
                user.subtype = new cls(JSON.parse(result['typeData']))
            }
            results.push(user)
        }
        return results
    }

    async fetchContacts(searchString: string): Promise<Contact[]> {
        const results: Contact[] = []
        const sql = new SQLBuilder()
        sql.addField('contacts.jsonData', 'jsonData')
        sql.addTable('contacts')
        sql.filterBy(
            'contacts.name LIKE ? OR contacts.email LIKE ? OR contacts.e164 LIKE ?',
            `${searchString}%`,
            `${searchString}%`,
            `+1${searchString}%`
        )
        sql.fk = Contact.fk
        const l = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of l) {
            const contact = new Contact(JSON.parse(result['jsonData']))
            results.push(contact)
        }
        return results
    }

    async fetchEmployeesForBookable(bookable: Bookable): Promise<User[]> {
        const booking = bookable instanceof Booking ? bookable : undefined
        const job = booking ? booking.job : undefined
        const lesson = bookable instanceof Lesson ? bookable : undefined
        const reservation = bookable instanceof Item ? bookable : undefined
        const order = bookable instanceof Order ? bookable : undefined
        const task = bookable instanceof Task ? bookable : undefined

        const employees: User[] = []
        if (booking) {
            for (const subJob of booking.jobs) {
                if (subJob.person) {
                    if (job?.person && job.person!.id === PSApplication.me().id)
                        employees.unshift(subJob.person)
                    else employees.push(subJob.person)
                }
            }
        }
        else if (
            (lesson && lesson.employee) ||
            (task && task.employee) ||
            (reservation && reservation.employee) ||
            (order && order.employee)
        )
            employees.push(bookable.employee!)

        return employees
    }

    async fetchColorForBookable(bookable: Bookable): Promise<string | undefined> {
        let employee = bookable && bookable.employee ? bookable.employee : undefined
        if (!employee) {
            const booking = bookable instanceof Booking ? bookable : undefined
            const job = booking ? booking.job : undefined
            employee = job ? job.person : undefined
        }
        const payee = employee
            ? await RecordFactory.getInstance().loadPayee(employee.id!)
            : undefined
        const color = payee ? payee.color : undefined
        return color ? color : bookable ? bookable.color : undefined
    }

    private async fetchCalendarKeys(): Promise<string[]> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addTable('calendar_keys')
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const calendarKeys: string[] = []
        for (const result of results) calendarKeys.push(result['id'])
        return calendarKeys
    }

    async fetchMessages(
        inbox: string,
        thread: BaseRecord | undefined,
        record: BaseRecord | undefined,
        forInbox: boolean,
        createdBeforeDate: moment.Moment | undefined
    ): Promise<Message[]> {
        const results: Message[] = []
        const sql = new SQLBuilder()
        sql.addField('messages.jsonData', 'jsonData')
        sql.addField('messages.createdDate', 'date')
        sql.addField(
            '(select max(m.createdDate) from messages as m where m.thread=messages.thread and m.owner=messages.owner and m.viewedDate is NULL)',
            'lastThreadUnreadDate'
        )
        sql.addTable('messages')
        sql.filterBy('messages.cancelledDate IS NULL')
        if (inbox) sql.filterBy('messages.owner = ?', inbox)
        if (thread) {
            if (!thread.id) return results
            sql.filterBy('messages.thread = ?', thread.id!)
        }
        else if (record) sql.filterBy('messages.record = ?', record.id!)
        else {
            const table = forInbox
                ? `(select thread, MAX(createdDate) as date from messages where owner = '${inbox}' and (sender != '${inbox}' OR subtype IS NULL OR subtype not in ('reminder', 'drip')) group by thread) as threads`
                : `(select thread, MAX(createdDate) as date from messages where owner = '${inbox}' group by thread) as threads`
            sql.addTable(
                table,
                'messages.thread=threads.thread and messages.createdDate=threads.date'
            )
        }
        if (createdBeforeDate)
            sql.filterBy('messages.createdDate < ?', createdBeforeDate.format('YYYY-MM-DD'))
        sql.orderBy('date', !thread)
        sql.fk = Message.fk

        const queryResults = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of queryResults) {
            const resultMap = new RecordDataMap(result)
            const message = new Message(JSON.parse(resultMap.get('jsonData')))
            results.push(message)
            if (result['lastThreadUnreadDate'])
                message.lastThreadUnreadDate = moment(
                    resultMap.valueForTimeStampKey('lastThreadUnreadDate')
                )
        }
        return results
    }

    async searchMessenger(keywords: string): Promise<BaseRecord[]> {
        const me = PSApplication.me().id!
        const teamSql = new SQLBuilder()
        teamSql.addField('teams.jsonData', 'jsonData')
        teamSql.addField("'team'", 'type')
        teamSql.addField('teams.createdDate', 'date')
        teamSql.addTable('teams')
        teamSql.filterBy('teams.cancelledDate is null')
        teamSql.filterBy('teams.name LIKE ?', `%${keywords}%`)
        teamSql.fk = Team.fk

        const userSql = new SQLBuilder()
        userSql.addField('users.jsonData', 'jsonData')
        userSql.addField("'user'", 'type')
        userSql.addField('users.createdDate', 'date')
        userSql.addTable('users')
        userSql.filterBy('users.name LIKE ?', `${keywords}%`)
        userSql.fk = User.fk
        const lastNameSql = userSql.clone()
        lastNameSql.filterBy('users.lastName LIKE ?', `${keywords}%`)
        userSql.addUnion(lastNameSql)

        teamSql.addUnion(userSql)
        teamSql.orderBy('date', false)

        const results: BaseRecord[] = []
        const queryResults = await SQLiteFactory.getInstance().executeSql(teamSql)
        for (const result of queryResults) {
            if (result['type'] === 'user') results.push(new User(JSON.parse(result['jsonData'])))
            else results.push(new Team(JSON.parse(result['jsonData'])))
        }

        return results
    }

    async fetchEmptyThreads(me: User): Promise<BaseRecord[]> {
        const oldestDate = moment().subtract(1, 'days')
        const sql = new SQLBuilder()
        sql.addField('users.jsonData', 'jsonData')
        sql.addField('clients.createdDate', 'createdDateForOwner')
        sql.addField("'user'", 'type')
        sql.addTable('clients')
        sql.addTable('users', 'clients.person=users.id')
        sql.filterBy('clients.owner = ?', me.id!)
        sql.filterBy('clients.createdDate > ?', oldestDate.format('YYYY-MM-DD'))
        sql.filterBy('clients.cancelledDate is null')
        sql.filterBy(
            'NOT EXISTS(select 1 from messages where thread=clients.person and owner = ?)',
            me.inbox!
        )
        sql.fk = User.fk

        if (me.perm === User.PERM_ROLE_CLIENT || me.perm > User.PERM_ROLE_EMPLOYEE) {
            const sql2 = new SQLBuilder()
            sql2.addField('users.jsonData', 'jsonData')
            sql2.addField('payees.createdDate', 'createdDateForOwner')
            sql2.addField("'user'", 'type')
            sql2.addTable('payees')
            sql2.addTable('users', 'payees.person=users.id')
            sql2.filterBy('payees.owner = ?', me.id!)
            if (me.perm > User.PERM_ROLE_EMPLOYEE)
                sql2.filterBy('payees.createdDate > ?', oldestDate.format('YYYY-MM-DD'))
            sql2.filterBy('payees.cancelledDate is null')
            sql2.filterBy(
                'NOT EXISTS(select 1 from messages where thread=payees.person and owner = ?)',
                me.inbox!
            )
            sql2.filterBy('payees.person != ?', me.id!)
            sql2.fk = User.fk
            sql.addUnion(sql2)
        }

        if (me.allowGroup) {
            const sql3 = new SQLBuilder()
            sql3.addField('teams.jsonData', 'jsonData')
            sql3.addField('teams.createdDate', 'createdDateForOwner')
            sql3.addField("'team'", 'type')
            sql3.addTable('teams')
            sql3.filterBy('teams.createdDate > ?', oldestDate.format('YYYY-MM-DD'))
            sql3.filterBy('teams.cancelledDate is null')
            sql3.filterBy('teams.owner = ?', me.id!)
            sql3.filterBy(
                'NOT EXISTS(select 1 from messages where thread=teams.id and owner = ?)',
                me.inbox!
            )
            sql3.fk = Team.fk
            sql.addUnion(sql3)
        }

        const results: BaseRecord[] = []
        const queryResults = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of queryResults) {
            const json = JSON.parse(result['jsonData'])
            json['created_date'] = result['createdDateForOwner']
            if (result['type'] === 'user') results.push(new User(json))
            else results.push(new Team(json))
        }
        return results
    }

    async fetchMessagesSinceLastModifiedDate(lastModifiedDate: moment.Moment): Promise<Message[]> {
        const sql = new SQLBuilder()
        sql.addField('messages.jsonData', 'jsonData')
        sql.addField('messages.createdDate', 'date')
        sql.addTable('messages')
        sql.filterBy('messages.owner = ?', PSApplication.inbox())
        sql.filterBy('messages.createdDate >= ?', lastModifiedDate.format('YYYY-MM-DD'))
        sql.orderBy('date')
        sql.fk = Message.fk
        const results: Message[] = []
        const queryResults = await SQLiteFactory.getInstance().executeSql(sql)
        for (const result of queryResults) results.push(new Message(JSON.parse(result['jsonData'])))
        return results
    }

    async hasMessageSinceLastModifiedDate(
        lastModifiedDate: moment.Moment,
        thread: BaseRecord
    ): Promise<boolean> {
        const sql = new SQLBuilder()
        sql.addField('messages.createdDate', 'date')
        sql.addTable('messages')
        sql.filterBy('messages.owner = ?', PSApplication.inbox())
        sql.filterBy('messages.createdDate >= ?', lastModifiedDate.format('YYYY-MM-DD'))
        if (thread.id) sql.filterBy('messages.thread = ?', thread.id)
        sql.orderBy('date')
        const queryResults = await SQLiteFactory.getInstance().executeSql(sql)
        return queryResults.length > 0
    }

    // todo: cameraEnabled
    // todo: cameraDisabled
    // todo: addrBookEnabled
    // todo: addrBookDisabled

    // todo: authorizeAddrBook

    // todo: calendarEnabled
    // todo: calendarDisabled
    // todo: locationEnabled
    // todo: bluetoothEnabled
    // todo: locationDisabled

    // todo: authNotification

    // todo: authCalendar

    // todo: fetchAllCalendars

    // todo: fetchAvailabilityCalendars
    // private async fetchAvailabilityCalendars(): Promise<DeviceCalendar[]> {
    //     const deviceCalendars = await this.fetchAllCalendars();
    //     const availabilityCalendars = PSApplication.device().calendars!;
    //     const l: DeviceCalendar[] = [];
    //     for (const deviceCalendar of deviceCalendars) {
    //         if (availabilityCalendars.includes(deviceCalendar.id!))
    //             l.push(deviceCalendar);
    //     }
    //     return l;
    // }

    // todo: defaultCalendarForPush
    // public async defaultCalendarForPush(): Promise<DeviceCalendar | undefined> {
    //     const deviceCalendars = await this.fetchAllCalendars();
    //     const defaultCalendar = PSApplication.device().calendar;
    //     if (defaultCalendar && deviceCalendars) {
    //         for (const deviceCalendar of deviceCalendars) {
    //             if (defaultCalendar === deviceCalendar.id)
    //                 return deviceCalendar;
    //         }
    //     }
    //     return undefined;
    // }

    // todo: fetchAvailability

    private makePlaceholders(len: number): string {
        if (len < 1) throw new Error('No placeholders')
        else {
            let sb = '?'
            for (let i = 1; i < len; i++) sb += ',?'
            return sb
        }
    }

    // todo: pushBookings - check this with mobile it has changed
    // async pushBookings(bookings: Bookable[], deleteAll: boolean): Promise<void> {
    //     if (!this.calendarEnabled())
    //         return;
    //     const deviceCalendar = await this.defaultCalendarForPush();
    //
    //     const me = PSApplication.me();
    //     const allowContactInfo = me && me.perm !== PERM_ROLE_CONTRACTOR;
    //
    //     const startDate = moment("2014-01-01");  // TODO: why isn't start date me.signupDate() or thereabouts?
    //     const untilDate = moment().add(3, 'months');
    //     const dtStart = startDate.valueOf();
    //     const dtEnd = untilDate.valueOf();
    //
    //     let selection = "((" + CalendarContract.Events.DTSTART + "> ?) AND (" + CalendarContract.Events.DTSTART + "< ?))";
    //     let selectionArgs = [dtStart.toString(), dtEnd.toString()];
    //     if (deviceCalendar) {
    //         selection = "((" + CalendarContract.Events.CALENDAR_ID + " = ?) " +
    //                 "AND (" + CalendarContract.Events.DTSTART + "> ?) AND (" + CalendarContract.Events.DTSTART + "< ?))";
    //         selectionArgs = [deviceCalendar.id!, dtStart.toString(), dtEnd.toString()];
    //     }
    //
    //     const cursor = await cr.query(eventUri, null, selection, selectionArgs, null);
    //     const myEvents: {[key: string]: any} = {};
    //     const myEventIds: {[key: string]: number} = {};
    //     const deleteMe: string[] = [];
    //
    //     const newKeys: string[] = [];
    //     const calendarKeys: {[key: string]: string} = {};
    //
    //     if (me) {
    //         for (const idString of await this.fetchCalendarKeys())
    //             calendarKeys[idString] = idString;
    //     }
    //
    //     if (cursor && cursor.length !== 0) {
    //         if (cursor.length > 0) {
    //             for (const row of cursor) {
    //                 const id = row[CalendarContract.Instances.EVENT_ID];
    //                 const psId = row[CalendarContract.Instances.DESCRIPTION];
    //                 if (psId && Event.isPS(psId)) {
    //                     const bookingId = psId.substring(psId.indexOf("PS-ID") + 7);
    //                     const eventInfo = myEvents[bookingId] || {};
    //                     myEventIds[bookingId] = id;
    //                     myEvents[bookingId] = eventInfo;
    //                     deleteMe.push(bookingId);
    //                 }
    //             }
    //         }
    //     }
    //
    //     let add = 0, update = 0, deleteCount = 0;
    //     if (deviceCalendar) {
    //         for (const booking of bookings) {
    //             if (!calendarKeys.hasOwnProperty(booking.id!))
    //                 continue;
    //             const instance = booking instanceof Booking ? booking : null;
    //             const item = instance ? instance.item : null;
    //             if (!me.client && instance && ("class" === instance.type || (item && "reservation" === item.type && item.startDate)))
    //                 continue;
    //             const bookingId = booking.id!;
    //             let eventInfo = myEvents[bookingId];
    //             if (!eventInfo)
    //                 eventInfo = {};
    //             if (newKeys.includes(bookingId))
    //                 continue;
    //             else if (booking.confirmedDate && !booking.cancelledDate) {
    //                 add += myEvents.hasOwnProperty(bookingId) ? 0 : 1;
    //                 update += myEvents.hasOwnProperty(bookingId) ? 1 : 0;
    //                 if (myEvents[bookingId])
    //                     deleteMe.splice(deleteMe.indexOf(bookingId), 1);
    //             }
    //             else
    //                 continue;
    //
    //             newKeys.push(bookingId);
    //             const person = Device.currentDevice().hipaa ? null :  booking.person;
    //             eventInfo[CalendarContract.Events.CALENDAR_ID] = deviceCalendar.id;
    //             eventInfo[CalendarContract.Events.TITLE] = booking.title;
    //             eventInfo[CalendarContract.Events.DTSTART] = DateUtil.dateToDT(booking.date);
    //             eventInfo[CalendarContract.Events.DTEND] = DateUtil.dateToDT(booking.endDate);
    //             eventInfo[CalendarContract.Events.EVENT_LOCATION] = booking.location;
    //             eventInfo[CalendarContract.Events.EVENT_TIMEZONE] = moment.tz.guess();
    //
    //             const description: string[] = [];
    //             if (person && person.name)
    //                 description.push(person.name);
    //             if (person && allowContactInfo) {
    //                 if (person.phone)
    //                     description.push(FormatLogic.formatPhoneNumber(person.phone));
    //                 if (person.email)
    //                     description.push(person.email);
    //             }
    //             if (instance) {
    //                 description.push(`Status: ${instance.statusString}`);
    //                 description.push(`Contact Date: ${moment(instance.createdDate).format('MMM D, YYYY')}`);
    //                 if (instance.conference && instance.allowConference)
    //                     description.push(`Join Conference: ${instance.conferenceUrlString}`);
    //                 if (instance.services && instance.services.length > 0) {
    //                     const names: string[] = [];
    //                     for (const service of instance.services)
    //                         if (service && service.name)
    //                             names.push(service.name);
    //                     if (names.length > 0)
    //                         description.push(`Service(s): ${names.join(', ')}`);
    //                 }
    //             }
    //             if (booking.memo)
    //                 description.push(booking.memo);
    //             description.push(`PS-ID: ${booking.id}`);
    //
    //             eventInfo[CalendarContract.Events.DESCRIPTION] = description.join("\r\n");
    //
    //             const eventId = myEventIds[bookingId];
    //             if (!eventId) {
    //                 await cr.insert(CalendarContract.Events.CONTENT_URI, eventInfo);
    //             }
    //             else {
    //                 const updateUri = ContentUris.withAppendedId(CalendarContract.Events.CONTENT_URI, eventId);
    //                 await cr.update(updateUri, eventInfo, null, null);
    //             }
    //         }
    //     }
    //
    //     for (const eventId of deleteMe) {
    //         if (!deleteAll) {
    //             if (calendarKeys.hasOwnProperty(eventId)) {
    //                 delete calendarKeys[eventId];
    //                 continue;
    //             }
    //         }
    //         deleteCount++;
    //         const deleteUri = ContentUris.withAppendedId(CalendarContract.Events.CONTENT_URI, myEventIds[eventId]);
    //         await cr.delete(deleteUri, null, null);
    //     }
    //
    //     if (add !== 0 || update !== 0 || deleteCount !== 0) {
    //         const alert = (add + update + deleteCount) > 1 ? `${add + update + deleteCount} updates to your calendar` : "1 update to your calendar";
    //         const activity = PSApplication.currentActivity();
    //         if (activity) {
    //             UIAlertActivity.showToast(alert, activity);
    //         }
    //     }
    // }

    async loadLastOrderedReader(): Promise<Reader | undefined> {
        const results = await RecordFactory.getInstance().loadReaders()
        for (let i = 0; i < results.length; i++) {
            const reader = results[i]
            if (reader.line1 && reader.line1.trim() !== '') {
                return reader
            }
        }
        return undefined
    }

    //todo: hasKeyguardManager
    //todo: hasGooglePlayServices
    //todo: supportsTapToPay

    async fetchAdjustmentHistory(item: Item): Promise<Adjustment[]> {
        const sql = new SQLBuilder()
        sql.addField('jsonData')
        sql.addTable('adjustments')
        sql.filterBy('adjustments.item=?', item.id!)
        sql.filterBy('cancelledDate IS NULL')
        sql.orderBy('createdDate', true)
        sql.fk = Adjustment.fk
        const results = await SQLiteFactory.getInstance().executeSql(sql)
        const adjustmentArray: Adjustment[] = []
        for (const adjustmentData of results) {
            adjustmentArray.push(new Adjustment(JSON.parse(adjustmentData['jsonData'])))
        }
        return adjustmentArray
    }

    async atLeastOnePublicItem(types: string[]): Promise<boolean> {
        const sql = new SQLBuilder()
        sql.addField('id')
        sql.addTable('items')
        sql.filterBy('items.owner = ?', PSApplication.owner().id!)
        sql.filterBy('items.cancelledDate IS NULL')
        sql.filterBy('items.isPublic')
        sql.filterBy('(items.endDate is null or items.endDate > CURRENT_DATE)')
        if (types.length > 0) {
            const args = new Array(types.length).fill('?').join(',')
            sql.filterByWithArgs(`items.type in (${args})`, types)
        }
        sql.fk = Item.fk
        const dbItems = await SQLiteFactory.getInstance().executeSql(sql)
        return dbItems.length > 0
    }
}
